import type { CardPinView, InstrumentRef } from "@screener/protocol";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  addCardPin,
  type CardPinAddResult,
  type CardPinRemoveResult,
  type CardPinsListResult,
  fetchCardPins,
  removeCardPin,
} from "./card-pins-api";
import { instrumentKey } from "./cards";

/**
 * Закрепления карточек §6.9 (ТЗ 1.52, §65): список пользователя целиком.
 * `loading` — ещё не прочитан, `unavailable` — демон не ответил; в обоих
 * случаях закреплённых карточек экран не показывает и значок просит
 * перечитать (design Д5, Д7).
 */
export type CardPinsState =
  | { status: "loading" }
  | { status: "ready"; pins: CardPinView[] }
  | { status: "unavailable" };

/**
 * `ignored` — ключ уже в пути, запрос не отправлялся; `unauthorized` — сессия
 * недействительна (хук уже сообщил об этом). Полосу экран ставит только на
 * `invalid` и `unavailable`.
 */
export type CardPinToggleOutcome = "ok" | "invalid" | "unavailable" | "ignored" | "unauthorized";

interface UseCardPinsOptions {
  onSessionInvalid: () => void;
}

interface UseCardPinsResult {
  state: CardPinsState;
  /** Закрепить карточку инструмента или снять закрепление. */
  toggle: (instrument: InstrumentRef, currentlyPinned: boolean) => Promise<CardPinToggleOutcome>;
  /** Ключи (`instrumentKey`), чьё действие ещё не завершилось, включая перечитывание. */
  pendingKeys: ReadonlySet<string>;
  /**
   * Перечитать список вручную: нажатие значка при непрочитанном списке.
   * `false` — демон не ответил (экран называет это полосой).
   */
  reload: () => Promise<boolean>;
}

/**
 * Состояние перед перечитыванием — тот же приём, что `beforeFavoritesReload`:
 * уже прочитанный список остаётся видимым на время `GET`.
 */
export function beforeCardPinsReload(previous: CardPinsState): CardPinsState {
  return previous.status === "ready" ? previous : { status: "loading" };
}

/** Подтверждённая демоном запись: `POST` вернул карточку, `DELETE` ответил `204`. */
export type ConfirmedCardPinWrite =
  | { kind: "added"; pin: CardPinView }
  | { kind: "removed"; instrument: InstrumentRef };

/**
 * Применить подтверждённую запись к готовому списку, когда перечитать его не
 * удалось: добавить или заменить по ключу, либо убрать ключ. Не готовый
 * список не трогается — применять не к чему.
 */
export function applyConfirmedWrite(
  previous: CardPinsState,
  write: ConfirmedCardPinWrite,
): CardPinsState {
  if (previous.status !== "ready") {
    return previous;
  }
  if (write.kind === "removed") {
    const key = instrumentKey(write.instrument);
    return {
      status: "ready",
      pins: previous.pins.filter((pin) => instrumentKey(pin.instrument) !== key),
    };
  }
  const key = instrumentKey(write.pin.instrument);
  const exists = previous.pins.some((pin) => instrumentKey(pin.instrument) === key);
  return {
    status: "ready",
    pins: exists
      ? previous.pins.map((pin) => (instrumentKey(pin.instrument) === key ? write.pin : pin))
      : [...previous.pins, write.pin],
  };
}

/**
 * Состояние после ответа на перечитывание. Неудача сразу после удавшейся
 * записи не прячет прочитанный список: запись подтверждена, а `unavailable`
 * убрал бы с экрана все закреплённые карточки.
 */
export function afterCardPinsReload(
  previous: CardPinsState,
  result: Exclude<CardPinsListResult, { status: "unauthorized" }>,
  afterWrite: boolean,
): CardPinsState {
  if (result.status === "ok") {
    return { status: "ready", pins: result.pins };
  }
  return afterWrite && previous.status === "ready" ? previous : { status: "unavailable" };
}

interface CardPinToggleDeps {
  add: (instrument: InstrumentRef) => Promise<CardPinAddResult>;
  remove: (instrument: InstrumentRef) => Promise<CardPinRemoveResult>;
  /** `true` — список перечитан (или ответ устарел), `false` — демон не ответил. */
  reload: () => Promise<boolean>;
  /** Применить подтверждённую запись локально, если перечитать не удалось. */
  applyWrite: (write: ConfirmedCardPinWrite) => void;
}

/**
 * Последовательность действия: запись (add/remove), на успехе — перечитывание
 * списка и только потом конец. Чистая функция ради теста гонки, как
 * `sendFavoriteToggle`: зависимости переданы параметром.
 */
export async function sendCardPinToggle(
  instrument: InstrumentRef,
  currentlyPinned: boolean,
  deps: CardPinToggleDeps,
): Promise<CardPinToggleOutcome> {
  if (currentlyPinned) {
    const result = await deps.remove(instrument);
    if (result.status === "ok" && !(await deps.reload())) {
      deps.applyWrite({ kind: "removed", instrument });
    }
    return result.status;
  }
  const result = await deps.add(instrument);
  if (result.status === "ok" && !(await deps.reload())) {
    deps.applyWrite({ kind: "added", pin: result.pin });
  }
  return result.status;
}

/**
 * Занятость ключа держится этим множеством, а не его копией: второй вызов
 * того же ключа, пока первый (запись плюс перечитывание) в пути, возвращает
 * `null` — второй запрос не отправляется.
 */
export function guardedCardPinToggle(
  pending: Set<string>,
  instrument: InstrumentRef,
  currentlyPinned: boolean,
  deps: CardPinToggleDeps,
): Promise<CardPinToggleOutcome> | null {
  const key = instrumentKey(instrument);
  if (pending.has(key)) {
    return null;
  }
  pending.add(key);
  return sendCardPinToggle(instrument, currentlyPinned, deps).finally(() => pending.delete(key));
}

/**
 * Перечитывание с защитой от устаревшего ответа: ответ вызова, чей номер
 * меньше номера последнего отправленного, отбрасывается (`null`).
 */
export function createCardPinsReloader(
  fetch: () => Promise<CardPinsListResult>,
): () => Promise<CardPinsListResult | null> {
  let latestRequestId = 0;
  return async () => {
    latestRequestId += 1;
    const requestId = latestRequestId;
    const result = await fetch();
    return requestId === latestRequestId ? result : null;
  };
}

/**
 * Закрепления карточек списком пользователя: загрузка при входе,
 * перезагрузка после собственного действия, без опроса — как `useFavorites`.
 */
export function useCardPins({ onSessionInvalid }: UseCardPinsOptions): UseCardPinsResult {
  const [state, setState] = useState<CardPinsState>({ status: "loading" });
  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(new Set());
  const pendingRef = useRef<Set<string>>(new Set());
  const reloadRef = useRef(createCardPinsReloader(fetchCardPins));

  const load = useCallback(
    (afterWrite: boolean): Promise<boolean> => {
      setState(beforeCardPinsReload);
      return reloadRef.current().then((result) => {
        if (result === null) {
          return true;
        }
        if (result.status === "unauthorized") {
          onSessionInvalid();
          return true;
        }
        setState((previous) => afterCardPinsReload(previous, result, afterWrite));
        return result.status === "ok";
      });
    },
    [onSessionInvalid],
  );

  const reload = useCallback(() => load(false), [load]);

  useEffect(() => {
    load(false);
  }, [load]);

  const toggle = useCallback(
    async (instrument: InstrumentRef, currentlyPinned: boolean): Promise<CardPinToggleOutcome> => {
      const outcome = guardedCardPinToggle(pendingRef.current, instrument, currentlyPinned, {
        add: addCardPin,
        remove: removeCardPin,
        reload: () => load(true),
        applyWrite: (write) => setState((previous) => applyConfirmedWrite(previous, write)),
      });
      if (outcome === null) {
        // Ключ уже в пути — экран это проверяет сам, это крайний случай той же гонки.
        return "ignored";
      }
      setPendingKeys(new Set(pendingRef.current));
      const result = await outcome;
      setPendingKeys(new Set(pendingRef.current));
      if (result === "unauthorized") {
        onSessionInvalid();
      }
      return result;
    },
    [load, onSessionInvalid],
  );

  return { state, toggle, pendingKeys, reload };
}
